import { useState } from "react";
import {
  ArrowUpRight,
  Database,
  RefreshCcw,
  Layers,
  ShieldCheck,
  Workflow,
  Users,
  AlertTriangle,
  Compass,
  FileText,
  Network,
  Mail,
  Sparkles,
  CheckCircle2,
  Bell,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";
import LeadModal from "../../components/public/LeadModal";
import { IconVisual } from "./MarketingHome";

export default function Features() {
  const [leadOpen, setLeadOpen] = useState(false);

  return (
    <PublicLayout title="Features">
      <Hero onConnect={() => setLeadOpen(true)} />
      <CapabilityGroups />
      <ReconciliationStrip />
      <OperationsGroups />
      <ContinuityBand />
      <ClosingCTA onConnect={() => setLeadOpen(true)} />
      {leadOpen && (
        <LeadModal
          onClose={() => setLeadOpen(false)}
          source="features_page"
          title="Let's walk through Kinntegra together."
          subtitle="A short call from our team — we'll show you how each module fits your practice."
        />
      )}
    </PublicLayout>
  );
}

/* ─────────────────── HERO ─────────────────── */
function Hero({ onConnect }) {
  return (
    <section data-testid="features-hero" className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C8A96B]/[0.06] blur-[160px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-6 md:pt-10 pb-16 md:pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] tracking-[0.2em] uppercase text-white/70 mb-8">
              <Sparkles size={12} className="text-[#C8A96B]" />
              What Kinntegra does
            </div>
            <h1 className="font-light text-[40px] leading-[1.06] sm:text-[56px] lg:text-[68px] tracking-[-0.025em]">
              Every layer of a
              <br />
              <span className="font-normal italic text-[#C8A96B]">
                distribution practice.
              </span>
            </h1>
            <p className="mt-10 text-lg md:text-xl text-white/70 max-w-2xl leading-[1.55] font-light">
              Reporting infrastructure, MFD operations, continuity systems,
              and succession planning — designed and integrated as one
              cohesive platform.
            </p>
            <div className="mt-12">
              <button
                onClick={onConnect}
                data-testid="features-hero-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition active:scale-[0.98]"
              >
                Connect With Us
                <ArrowUpRight size={16} />
              </button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <IconVisual
              items={[
                { icon: Database, label: "Data Layer" },
                { icon: Layers, label: "FIFO Lots" },
                { icon: Workflow, label: "Operations" },
                { icon: Compass, label: "Continuity" },
                { icon: FileText, label: "Audit Trail" },
                { icon: Network, label: "Reconciled" },
              ]}
              eyebrow="One cohesive platform"
              caption="Reconciled data, operations, continuity, succession — integrated end-to-end."
              testId="features-hero-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CAPABILITY GROUPS (Data layer) ─────────────────── */
function CapabilityGroups() {
  const items = [
    {
      icon: Database,
      title: "Unified data ingestion",
      copy: "Karvy (KFintech) and CAMS feeds normalised into a single, reconciled investor master and AUM master — no manual stitching.",
    },
    {
      icon: RefreshCcw,
      title: "Daily multi-broker reconciliation",
      copy: "WBR2 / WBR9 / MFSD feeds ingested overnight, matched against folios, with exception logs surfaced for review.",
    },
    {
      icon: Layers,
      title: "FIFO active-units tracking",
      copy: "Every redemption mapped back to its purchase lot. XIRR, unrealised gains and holding period reflect actual cohorts.",
    },
    {
      icon: AlertTriangle,
      title: "Unit-mismatch detection",
      copy: "Cached, deterministic mismatch detection between registrar units and FIFO units — orphan-suppressed to kill false positives.",
    },
  ];
  return (
    <section
      data-testid="features-data-layer"
      className="py-16 md:py-20 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Eyebrow>The data layer</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
          Reconciled, lot-level, audit-grade.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {items.map((it) => (
            <FeatureCard key={it.title} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── RECONCILIATION STRIP ─────────────────── */
function ReconciliationStrip() {
  const labels = [
    { v: "40,716", l: "Folios reconciled" },
    { v: "₹711 Cr", l: "Live AUM tracked" },
    { v: "5", l: "Broker feeds unified" },
    { v: "100%", l: "Field-change audit-logged" },
  ];
  return (
    <section className="py-12 md:py-16 border-b border-white/5" data-testid="features-strip">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {labels.map((s) => (
            <div key={s.l}>
              <div className="text-3xl md:text-4xl font-light text-white tabular-nums">
                {s.v}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-white/45">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── OPERATIONS / SUCCESSION GROUPS ─────────────────── */
function OperationsGroups() {
  const ops = [
    {
      icon: Workflow,
      title: "SIP / STP / SWP books",
      copy: "Live order books with exception flags — bounces, OTM failures, ARN-rejections — visible the moment they happen.",
    },
    {
      icon: Users,
      title: "Multi-associate workflows",
      copy: "Sub-broker / associate roles with scoped client books and per-associate AUM attribution — built for firms, not solos.",
    },
    {
      icon: Mail,
      title: "Client reporting preferences",
      copy: "Per-client cadence, channel, and content. Email / WhatsApp. Configured once, runs forever.",
    },
    {
      icon: FileText,
      title: "Folio-level field tracking",
      copy: "Bank, nominee, contact, communication mode — every field versioned and audit-logged at the folio level.",
    },
    {
      icon: Bell,
      title: "Cronjobs & alerts",
      copy: "Scheduled feed pulls, email-watchlist scanners, dashboard rebuilds, and stuck-job recovery — surfaced as health-tracked cron pipelines.",
    },
    {
      icon: Network,
      title: "Sub-broker mapping",
      copy: "Map clients to associates with cascade-safe tagging / un-tagging. Ghost entries are caught before they enter the books.",
    },
  ];
  return (
    <section
      data-testid="features-operations"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Eyebrow>Operations</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
          Everything your firm runs on day-to-day.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {ops.map((it) => (
            <FeatureCard key={it.title} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CONTINUITY BAND ─────────────────── */
function ContinuityBand() {
  const items = [
    {
      icon: Compass,
      title: "Continuity infrastructure",
      copy: "Practice-wide field audit logs, recoverable history, and structured handover workflows — so the practice outlives any single moment.",
    },
    {
      icon: ShieldCheck,
      title: "Succession readiness",
      copy: "Nominee, bank, and contact data versioned and surfaced at family, folio, and firm level — ready for transitions whenever they happen.",
    },
    {
      icon: CheckCircle2,
      title: "Independence preserved",
      copy: "Kinntegra is your infrastructure. Your brand, your clients, your firm — strengthened, not absorbed.",
    },
  ];
  return (
    <section
      data-testid="features-continuity"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Eyebrow>Continuity & succession</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
          Built for the long arc of a practice.
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {items.map((it) => (
            <FeatureCard key={it.title} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, copy }) {
  return (
    <div
      data-testid={`features-card-${title.slice(0, 14).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group bg-[#07111F] p-8 md:p-10 hover:bg-[#0c1827] transition relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#C8A96B]/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
      <Icon size={22} className="text-[#C8A96B] mb-7" strokeWidth={1.4} />
      <h3 className="text-lg font-light text-white mb-3 tracking-tight leading-snug">
        {title}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed">{copy}</p>
    </div>
  );
}

function ClosingCTA({ onConnect }) {
  return (
    <section
      data-testid="features-cta"
      className="py-20 md:py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[320px] w-[640px] rounded-full bg-[#C8A96B]/[0.05] blur-[120px]" />
      </div>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] leading-[1.15]">
          See it in your context.
        </h2>
        <p className="mt-6 text-base md:text-lg text-white/55 font-light leading-[1.7] max-w-xl mx-auto">
          We'll walk you through Kinntegra against your actual data
          shape, AUM size, and operating cadence.
        </p>
        <div className="mt-10 flex justify-center">
          <button
            onClick={onConnect}
            data-testid="features-cta-final"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
          >
            Schedule a Call
            <ArrowUpRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
      <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
      {children}
    </div>
  );
}
